import React, { Component } from "react";
import "./DragSortableList.css";

/**
 * DragSortableList — Envoltura reutilizable de drag&drop por índices
 *
 * Props:
 *  - items: array de elementos
 *  - orderProp: string (por defecto "sortOrder")
 *  - onChange(updatedArray): callback tras reordenar (se reindexa orderProp = i+1)
 *  - getKey(item, idx): fn para clave estable; por defecto usa orderProp || idx
 *  - renderItem(item, idx, state): fn que devuelve el contenido visual de cada ítem
 *
 * Comportamiento:
 *  - Pinta SIEMPRE ordenado por orderProp (si existe), sino por el índice
 *  - Al soltar (drop): mueve el ítem, reindexa orderProp => 1..N, hace setState y onChange(updated)
 *  - Añade clases CSS: .dsl-card, .is-over, .is-dragging para animaciones
 */
const sortByOrder = (items = [], orderProp = "sortOrder") => {
  const a = (items || []).slice();
  a.sort((x, y) => {
    const sx = Number(x?.[orderProp]);
    const sy = Number(y?.[orderProp]);
    const vx = Number.isFinite(sx) && sx > 0 ? sx : Number.MAX_SAFE_INTEGER;
    const vy = Number.isFinite(sy) && sy > 0 ? sy : Number.MAX_SAFE_INTEGER;
    return vx - vy;
  });
  return a;
};
const reindex = (arr = [], orderProp = "sortOrder") =>
  arr.map((it, i) => ({ ...it, [orderProp]: i + 1 }));

export default class DragSortableList extends Component {
  static defaultProps = {
    items: [],
    orderProp: "sortOrder",
    getKey: (item, idx, orderProp) => item?.[orderProp] ?? idx,
  };

  constructor(props) {
    super(props);
    const items = reindex(sortByOrder(props.items, props.orderProp), props.orderProp);
    this.state = {
      items,
      dragIndex: null,
      overIndex: null,
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.items !== this.props.items && this.state.dragIndex == null) {
      const items = reindex(
        sortByOrder(this.props.items || [], this.props.orderProp),
        this.props.orderProp
      );
      this.setState({ items, dragIndex: null, overIndex: null });
    }
  }

  // DnD
  onDragStart = (index) => (e) => {
    this.setState({ dragIndex: index, overIndex: index });
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", String(index)); } catch {}
  };
  onDragEnter = (index) => (e) => {
    e.preventDefault();
    if (index !== this.state.overIndex) this.setState({ overIndex: index });
  };
  onDragOver = (index) => (e) => {
    e.preventDefault();
    if (index !== this.state.overIndex) this.setState({ overIndex: index });
  };
  onDragLeave = () => {
    // mantenemos overIndex si se mueve entre hijos; se limpiará en drop o end
  };
  onDrop = (index) => (e) => {
    e.preventDefault();
    const { orderProp, onChange } = this.props;
    let from = Number(e.dataTransfer.getData("text/plain"));
    if (!Number.isFinite(from)) from = this.state.dragIndex;
    const to = index;

    if (from == null || from === to) {
      this.setState({ dragIndex: null, overIndex: null });
      return;
    }

    const items = this.state.items.slice();
    const [moved] = items.splice(from, 1);
    items.splice(to, 0, moved);

    const updated = reindex(items, orderProp);
    this.setState({ items: updated, dragIndex: null, overIndex: null });
    onChange && onChange(updated);
  };
  onDragEnd = () => this.setState({ dragIndex: null, overIndex: null });

  render() {
    const { renderItem, getKey, orderProp } = this.props;
    const list = sortByOrder(this.state.items, orderProp);

    return (
      <div className="dsl-grid">
        {list.map((item, idx) => {
          const key = getKey(item, idx, orderProp);
          const dragging = this.state.dragIndex === idx;
          const over = this.state.overIndex === idx;
          return (
            <div
              key={key}
              className={`dsl-card ${dragging ? "is-dragging" : ""} ${over ? "is-over" : ""}`}
              draggable
              onDragStart={this.onDragStart(idx)}
              onDragEnter={this.onDragEnter(idx)}
              onDragOver={this.onDragOver(idx)}
              onDragLeave={this.onDragLeave}
              onDrop={this.onDrop(idx)}
              onDragEnd={this.onDragEnd}
            >
              {renderItem ? renderItem(item, idx, { dragging, over }) : null}
            </div>
          );
        })}
      </div>
    );
  }
}
